import React, { useCallback, useState } from "react";
import noVideosmIcon from "@assets/images/noVideosmIcon.webp";
import "react-tooltip/dist/react-tooltip.css";
import { convertSecondsIntoHoursWithoutZero } from "@src/utils/TimerHelper";
import { getValueOrDefault } from "@src/utils/ConditionHelper";
import PlayIconAudio from "@assets/images/PlayIconAudio.svg";
import { useNavigate } from "react-router-dom";
import VideoBorder from "@assets/images/borders/video-border.webp";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useVideoManager from "@src/hooks/useVideoManager";
import { useLiveQuery } from "dexie-react-hooks";
import { getDb } from "@src/db/EpisodesDb";
import type { IBookmarkEpisodes, IBookmarkEpisodesItems } from "@src/types/AppTypes";

const BookmarkEpisodes: React.FC<IBookmarkEpisodes> = ({
  item,
  index,
  removeEpisodeOrMusicMutate,
  handleClickOnAddToCartEpisode,
}) => {
  const downloadedEpisode = useLiveQuery(async () => {
    const ep = await getDb()?.episodes?.get(item.id);
    return ep;
  }, [item.id]);
  const videoManager = useVideoManager();
  const navigate = useNavigate();
  const [isBookmarkedEpisodeImageLoading, setIsBookmarkedEpisodeImageLoading] =
    useState<boolean>(true);

  const onEpisodePlayClick = useCallback(
    async (items: IBookmarkEpisodesItems) => {
      sessionStorage.setItem("prevroute", location.pathname);
      videoManager?.setItem({ ...items });
      navigate("/episode/" + items?.slug);
    },
    [navigate, videoManager]
  );

  return (
    <>
      <div
        className="flex items-center justify-between relative max-md:p-4 max-sm:p-2 max-md:border max-md:border-[#89D9EE] max-md:rounded-xl"
        id={`episode-${item.id}`}
      >
        <div
          className="grid grid-cols-[192px_1fr] max-lg:grid-cols-[100px_1fr] max-sm:grid-cols-[80px_1fr] items-center gap-3 w-full"
          key={item.id}
        >
          <div
            className="p-1 h-32 max-md:h-24 relative rounded-[5px_15px_15px_20px] max-sm:rounded-[5px_10px_10px_10px] overflow-hidden cursor-pointer"
            onClick={() => {
              if (downloadedEpisode?.downloaded) {
                onEpisodePlayClick({ ...item, ...downloadedEpisode });
              } else {
                onEpisodePlayClick(item);
              }
            }}
          >
            {isBookmarkedEpisodeImageLoading && (
              <div className="skeleton p-1 top-0 left-0 size-full absolute"></div>
            )}
            <img
              className="size-full rounded-xl object-cover absolute top-0 left-0 blur-sm"
              src={getValueOrDefault(
                getImageUrlWithExpiry(item.thumbnail),
                PlayIconAudio
              )}
              onLoad={() => {
                setIsBookmarkedEpisodeImageLoading(false);
              }}
              onError={() => {
                setIsBookmarkedEpisodeImageLoading(false);
              }}
              alt="bookmark-item-img"
            />
            <img
              className="size-full rounded-xl object-contain relative z-[1]"
              src={getValueOrDefault(
                getImageUrlWithExpiry(item.thumbnail),
                PlayIconAudio
              )}
              onLoad={() => {
                setIsBookmarkedEpisodeImageLoading(false);
              }}
              onError={() => {
                setIsBookmarkedEpisodeImageLoading(false);
              }}
              alt="bookmark-item-img"
            />
            <img
              className="absolute size-full top-0 left-0 z-[3]"
              src={VideoBorder}
              alt="border"
            />
          </div>
          <div className="flex flex-col items-start md:pr-3">
            <div
              className="flex items-center gap-3 mb-1 max-sm:mb-1 max-sm:gap-1 cursor-pointer"
              onClick={() => {
                if (downloadedEpisode?.downloaded) {
                  onEpisodePlayClick({ ...item, ...downloadedEpisode });
                } else {
                  onEpisodePlayClick(item);
                }
              }}
            >
              <img
                src={noVideosmIcon}
                alt="video-icon"
                className="max-sm:w-5 w-7"
              />
              <h6 className="text-[#C52E95] text-xl max-sm:text-base">Video</h6>
            </div>
            <h6
              className="text-primary w-[calc(100%-75px)] break-words break-all line-clamp-1 max-md:text-xl max-sm:text-base text-xl cursor-pointer"
              onClick={() => {
                if (downloadedEpisode?.downloaded) {
                  onEpisodePlayClick({ ...item, ...downloadedEpisode });
                } else {
                  onEpisodePlayClick(item);
                }
              }}
            >
              {"S" +
                item.number_of_season.toString() +
                " - Episode " +
                item.number_of_episode +
                ". " +
                convertSecondsIntoHoursWithoutZero(item.total_duration, 0)
                  .timeInWords +
                " . " +
                item?.release_date}
            </h6>
            <div className="flex items-center justify-between w-full">
              <h6
                className="text-primary break-words break-all line-clamp-1 max-md:text-xl text-xl max-sm:text-base w-[calc(100%-75px)] cursor-pointer"
                onClick={() => {
                  if (downloadedEpisode?.downloaded) {
                    onEpisodePlayClick({ ...item, ...downloadedEpisode });
                  } else {
                    onEpisodePlayClick(item);
                  }
                }}
              >
                {item.title}
              </h6>
              <div className="flex items-center justify-between gap-2">
                {item.is_purchased ? (
                  <button
                    className="text-primary text-3xl"
                    onClick={() => {
                      if (downloadedEpisode?.downloaded) {
                        onEpisodePlayClick({ ...item, ...downloadedEpisode });
                      } else {
                        onEpisodePlayClick(item);
                      }
                    }}
                    aria-label="Play"
                  >
                    <i className="ri-play-circle-line"></i>
                  </button>
                ) : item.is_rented ? (
                  <button
                    className="text-primary text-3xl"
                    onClick={() => {
                      if (downloadedEpisode?.downloaded) {
                        onEpisodePlayClick({ ...item, ...downloadedEpisode });
                      } else {
                        onEpisodePlayClick(item);
                      }
                    }}
                    aria-label="Play"
                  >
                    <i className="ri-play-circle-line"></i>
                  </button>
                ) : (
                  <button
                    className="flex min-w-[125px] py-3 px-2 text-sm justify-center text-[#1D1159] bg-[#d854ba] bg-[url('@assets/ui-helpers/button/ButtonDarkBorder.png')] rounded-3xl bg-no-repeat bg-cover bg-center duration-150 hover:scale-95 disabled:hover:scale-100 disabled:opacity-80 disabled:cursor-not-allowed disabled:duration-0 max-sm:bg-none max-sm:min-w-[auto] max-sm:bg-transparent max-sm:p-0"
                    onClick={() => {
                      handleClickOnAddToCartEpisode(item, index);
                    }}
                    disabled={item.is_added_purchased || item.is_added_rented}
                    aria-label="Add to cart"
                  >
                    {item.is_added_purchased || item.is_added_rented ? (
                      <>
                        <span className="max-sm:hidden">Added</span>
                        <svg
                          className="sm:hidden"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.25 22.5C17.2165 22.5 18 21.7165 18 20.75C18 19.7835 17.2165 19 16.25 19C15.2835 19 14.5 19.7835 14.5 20.75C14.5 21.7165 15.2835 22.5 16.25 22.5Z"
                            fill="#ffbff7"
                          />
                          <path
                            d="M8.25 22.5C9.2165 22.5 10 21.7165 10 20.75C10 19.7835 9.2165 19 8.25 19C7.2835 19 6.5 19.7835 6.5 20.75C6.5 21.7165 7.2835 22.5 8.25 22.5Z"
                            fill="#ffbff7"
                          />
                          <path
                            d="M4.84 3.94L4.64 6.39C4.6 6.86 4.97 7.25 5.44 7.25H20.75C21.17 7.25 21.52 6.93 21.55 6.51C21.68 4.74 20.33 3.3 18.56 3.3H6.27C6.17 2.86 5.97 2.44 5.66 2.09C5.16 1.56 4.46 1.25 3.74 1.25H2C1.59 1.25 1.25 1.59 1.25 2C1.25 2.41 1.59 2.75 2 2.75H3.74C4.05 2.75 4.34 2.88 4.55 3.1C4.76 3.33 4.86 3.63 4.84 3.94Z"
                            fill="#ffbff7"
                          />
                          <path
                            d="M20.5101 8.75H5.17005C4.75005 8.75 4.41005 9.07 4.37005 9.48L4.01005 13.83C3.87005 15.54 5.21005 17 6.92005 17H18.0401C19.5401 17 20.8601 15.77 20.9701 14.27L21.3001 9.6C21.3401 9.14 20.9801 8.75 20.5101 8.75Z"
                            fill="#ffbff7"
                          />
                        </svg>
                      </>
                    ) : (
                      <>
                        <span className="max-sm:hidden">Add to cart</span>
                        <svg
                          className="sm:hidden"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 2H3.74001C4.82001 2 5.67 2.93 5.58 4L4.75 13.96C4.61 15.59 5.89999 16.99 7.53999 16.99H18.19C19.63 16.99 20.89 15.81 21 14.38L21.54 6.88C21.66 5.22 20.4 3.87 18.73 3.87H5.82001"
                            stroke="#ffbff7"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.25 22C16.9404 22 17.5 21.4404 17.5 20.75C17.5 20.0596 16.9404 19.5 16.25 19.5C15.5596 19.5 15 20.0596 15 20.75C15 21.4404 15.5596 22 16.25 22Z"
                            stroke="#ffbff7"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M8.25 22C8.94036 22 9.5 21.4404 9.5 20.75C9.5 20.0596 8.94036 19.5 8.25 19.5C7.55964 19.5 7 20.0596 7 20.75C7 21.4404 7.55964 22 8.25 22Z"
                            stroke="#ffbff7"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M9 8H21"
                            stroke="#ffbff7"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                )}
                {/* {!item.is_purchased && (
                 
                )} */}
                {/* {(item.is_purchased ||
                  item.is_rented ||
                  !item.is_rent_expired) && (
                  
                )} */}
                <button
                  onClick={() => {
                    removeEpisodeOrMusicMutate({
                      data: { saveable_id: item.id, saveable_type: "Episode" },
                    });
                  }}
                  className="flex items-center gap-1 text-secondary duration-150 hover:scale-95"
                  aria-label="Remove"
                >
                  <i className="ri-delete-bin-6-line text-primary-gradient text-[26px] max-sm:text-xl"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-no-repeat bg-contain bg-center bg-[url('@assets/images/ContactBorder.png')] w-full h-6 last:hidden max-md:hidden flex-shrink-0"></div>
    </>
  );
};

export default BookmarkEpisodes;
